import { FetchResult } from "@apollo/client";
import {
	CreateSalaryInformationDocument as CREATE_SALARY_INFORMATION,
	CreatePaymentInformationDocument as CREATE_PAYMENT_INFORMATION,
	CreateWorkingConditionInformationDocument as CREATE_WORKING_CONDITION_INFORMATION,
	useCreateBillingInformationMutation,
	useUpdateBillingInformationMutation,
	CreateContactPersonInformationDocument as CREATE_CONTACT_PERSON_INFORMATION,
	UpdateContactPersonInformationDocument as UPDATE_CONTACT_PERSON_INFORMATION,
	UpdatePaymentInformationDocument as UPDATE_PAYMENT_INFORMATION,
	UpdateSalaryInformationDocument as UPDATE_SALARY_INFORMATION,
	UpdateWorkingConditionInformationDocument as UPDATE_WORKING_CONDITION_INFORMATION,
	DeleteAdditionalInformationDocument as DELETE_ADDITIONAL_INFORMATION,
	CreateContactEmailDocument as CREATE_CONTACT_EMAIL,
	CreateCompanyContactDocument as CREATE_COMPANY_CONTACT,
	CreatePersonContactDocument as CREATE_PERSON_CONTACT,
	UpdateCompanyContactDocument as UPDATE_COMPANY_CONTACT,
	UpdatePersonContactDocument as UPDATE_PERSON_CONTACT,
	UpdateContactEmailDocument as UPDATE_CONTACT_EMAIL,
	CreateContactAddressDocument as CREATE_CONTACT_ADDRESS,
	UpdateContactAddressDocument as UPDATE_CONTACT_ADDRESS,
	CreatePhoneInformationDocument as CREATE_PHONE_INFORMATION,
	UpdatePhoneInformationDocument as UPDATE_PHONE_INFORMATION,
	CreateContractedInformationDocument as CREATE_CONTRACTED_INFORMATION,
	UpdateContractedInformationDocument as UPDATE_CONTRACTED_INFORMATION,
	CreateInternalWorkingConditionDocument as CREATE_INTERNAL_WORKING_CONDITION,
	UpdateInternalWorkingConditionDocument as UPDATE_INTERNAL_WORKING_CONDITION,
	// CreateIsContactPersonInformationDocument as CREATE_IS_CONTACT_PERSON_OF_INFORMATION,
	// UpdateIsContactPersonInformationDocument as UPDATE_IS_CONTACT_PERSON_OF_INFORMATION,
	CreateWebsiteInformationDocument as CREATE_WEBSITE_INFORMATION,
	UpdateWebsiteInformationDocument as UPDATE_WEBSITE_INFORMATION,
	CreatePaymentConditionInformationDocument as CREATE_PAYMENT_CONDITION_INFORMATION,
	UpdatePaymentConditionInformationDocument as UPDATE_PAYMENT_CONDITION_INFORMATION,
	CreateDateInformationDocument as CREATE_DATE_INFORMATION,
	UpdateDateInformationDocument as UPDATE_DATE_INFORMATION,
	CreateDepartmentInformationDocument as CREATE_DEPARTMENT_INFORMATION,
	UpdateDepartmentInformationDocument as UPDATE_DEPARTMENT_INFORMATION,
	Contact,
	AddressInformation,
	EmailInformation,
	ContactPersonInformation,
	BillingInformation,
	PaymentInformation,
	PhoneInformation,
	SalaryInformation,
	WorkingConditionInformation,
	ContractedInformation,
	EmployeeWorkingConditionInformation,
	WebsiteInformation,
	PaymentConditionInformation,
	IsContactPersonOfInformation,
	DepartmentInformation,
	DateInformation,
	CreateBillingInformationMutationVariables,
	UpdateBillingInformationMutation,
	UpdateBillingInformationMutationVariables,
	CreateBillingInformationMutation,
} from "@repo/server-api";

import { findExactSingleContactType, useMutationWithPromise } from "../helper/Index";

export const useCreateContactEmails = () => {
	const createContactEmail = useMutationWithPromise(CREATE_CONTACT_EMAIL);

	const doWork = async (contactId: string, email: EmailInformation) => {
		createContactEmail({
			contactId: contactId,
			emailAddress: email.emailAddress,
			emailType: email.emailType,
		});
	};

	return [doWork];
};

export const useCreateContactAddresses = () => {
	const createContactAddress = useMutationWithPromise(CREATE_CONTACT_ADDRESS);

	const doWork = async (contactId: string, address: AddressInformation) => {
		createContactAddress({
			contactId: contactId,
			type: address.type,
			name: address.name,
			city: address.city,
			country: address.country,
			state: address.state,
			street: address.street,
			zipCode: address.zipCode,
		});
	};

	return [doWork];
};

export const useCreateContactPersonInformation = () => {
	const createContactPersonInformation = useMutationWithPromise(CREATE_CONTACT_PERSON_INFORMATION);

	const doWork = async (contactId: string, personInformation: ContactPersonInformation) => {
		createContactPersonInformation({
			contactId: contactId,
			contactUsage: personInformation.contactUsage,
			contactPersonId: personInformation.contactPersonId,
		});
	};

	return [doWork];
};

export const useCreateBillingInformation = () => {
	const [createBillingInformation] = useCreateBillingInformationMutation();

	const doWork = (
		contactId: string,
		billingInformation: BillingInformation,
	): Promise<FetchResult<CreateBillingInformationMutation>> => {
		const variables: CreateBillingInformationMutationVariables = {
			contactId: contactId,
			name: billingInformation.name,
			billingAddress: {
				type: billingInformation.billingAddress.type,
				city: billingInformation.billingAddress.city,
				country: billingInformation.billingAddress.country,
				state: billingInformation.billingAddress.state,
				street: billingInformation.billingAddress.street,
				zipCode: billingInformation.billingAddress.zipCode,
				name: billingInformation.billingAddress.name ?? "",
			},
			shippingAddress: {
				type: billingInformation.shippingAddress.type,
				city: billingInformation.shippingAddress.city,
				country: billingInformation.shippingAddress.country,
				state: billingInformation.shippingAddress.state,
				street: billingInformation.shippingAddress.street,
				zipCode: billingInformation.shippingAddress.zipCode,
				name: billingInformation.shippingAddress.name ?? "",
			},
			bankAccount: {
				bankName: billingInformation.bankAccount.bankName,
				bic: billingInformation.bankAccount.bic,
				clientReference: billingInformation.bankAccount.clientReference,
				clientReferenceDate: billingInformation.bankAccount.clientReferenceDate,
				iban: billingInformation.bankAccount.iban,
			},
		};

		return createBillingInformation({ variables: variables });
	};

	return [doWork];
};

export const useCreatePaymentInformation = () => {
	const createPaymentInformation = useMutationWithPromise(CREATE_PAYMENT_INFORMATION);

	const doWork = async (contactId: string, paymentInformation: PaymentInformation) => {
		createPaymentInformation({
			contactId: contactId,
			taxNumber: paymentInformation.taxNumber,
			paymentDeadlineInDays: paymentInformation.paymentDeadlineInDays,
			routeId: paymentInformation.routeId,
			vatId: paymentInformation.vatId,
		});
	};

	return [doWork];
};

export const useCreatePhoneInformation = () => {
	const createPhoneInformation = useMutationWithPromise(CREATE_PHONE_INFORMATION);

	const doWork = async (contactId: string, phoneInformation: PhoneInformation) => {
		createPhoneInformation({
			contactId: contactId,
			number: phoneInformation.number,
			phoneType: phoneInformation.phoneType,
		});
	};

	return [doWork];
};

export const useCreateSalaryInformation = () => {
	const createSalaryInformation = useMutationWithPromise(CREATE_SALARY_INFORMATION);

	const doWork = async (contactId: string, salaryInformation: SalaryInformation) => {
		createSalaryInformation({
			contactId: contactId,
			pricePerHour: salaryInformation.pricePerHour,
			startDate: salaryInformation.startDate,
		});
	};

	return [doWork];
};

export const useCreateEmployeeWorkingConditionInformation = () => {
	const createEmployeeWorkingConditionInformation = useMutationWithPromise(
		CREATE_INTERNAL_WORKING_CONDITION,
	);

	const doWork = async (
		contactId: string,
		internalWorkingCondition: EmployeeWorkingConditionInformation,
	): Promise<void> => {
		await createEmployeeWorkingConditionInformation({
			contactId: contactId,
			from: internalWorkingCondition.from,
			federalCode: internalWorkingCondition.federalLocation,
			workingTimeModelId: internalWorkingCondition.workingTimeModelId,
			to: internalWorkingCondition.to === "" ? null : internalWorkingCondition.to,
		});
	};

	return [doWork];
};

export const useUpdateEmployeeWorkingConditionInformation = () => {
	const udpateEmployeeWorkingConditionInformation = useMutationWithPromise(
		UPDATE_INTERNAL_WORKING_CONDITION,
	);

	const doWork = async (
		contactId: string,
		internalWorkingCondition: EmployeeWorkingConditionInformation,
	): Promise<object | unknown> => {
		return await udpateEmployeeWorkingConditionInformation({
			contactId: contactId,
			from: internalWorkingCondition.from,
			federalCode: internalWorkingCondition.federalLocation,
			workingTimeModelId: internalWorkingCondition.workingTimeModelId,
			to: internalWorkingCondition.to === "" ? null : internalWorkingCondition.to,
			id: internalWorkingCondition.id,
		});
	};

	return [doWork];
};

export const useCreateWorkingConditionInformation = () => {
	const createWorkingConditionInformation = useMutationWithPromise(
		CREATE_WORKING_CONDITION_INFORMATION,
	);

	const doWork = async (
		contactId: string,
		workingConditionInformation: WorkingConditionInformation,
	) => {
		createWorkingConditionInformation({
			contactId: contactId,
			hoursPerFriday: workingConditionInformation.hoursPerFriday,
			hoursPerMonday: workingConditionInformation.hoursPerMonday,
			hoursPerSaturday: workingConditionInformation.hoursPerSaturday,
			hoursPerSunday: workingConditionInformation.hoursPerSunday,
			hoursPerThursday: workingConditionInformation.hoursPerThursday,
			hoursPerTuesday: workingConditionInformation.hoursPerTuesday,
			hoursPerWednesday: workingConditionInformation.hoursPerWednesday,
			hoursPerWeek: workingConditionInformation.hoursPerWeek,
			isFlexible: workingConditionInformation.isFlexible,
			startDate: workingConditionInformation.startDate,
		});
	};

	return [doWork];
};

export const useCreateContractedInformation = () => {
	const createContractedInformation = useMutationWithPromise(CREATE_CONTRACTED_INFORMATION);

	const doWork = async (contactId: string, contractedInformation: ContractedInformation) => {
		createContractedInformation({
			contactId: contactId,
			contractedId: contractedInformation.contractedId,
			margin: contractedInformation.marge,
		});
	};

	return [doWork];
};

export const useUpdateContactPersonInformation = () => {
	const updateContactPersonInformation = useMutationWithPromise(UPDATE_CONTACT_PERSON_INFORMATION);

	const doWork = async (contactId: string, personInformation: ContactPersonInformation) => {
		updateContactPersonInformation({
			id: personInformation.id,
			contactId: contactId,
			contactUsage: personInformation.contactUsage,
			contactPersonId: personInformation.contactPersonId,
		});
	};

	return [doWork];
};

export const useUpdateBillingInformation = () => {
	const [updateBillingInformation] = useUpdateBillingInformationMutation();

	const doWork = (
		contactId: string,
		billingInformation: BillingInformation,
	): Promise<FetchResult<UpdateBillingInformationMutation>> => {
		const variables: UpdateBillingInformationMutationVariables = {
			id: billingInformation.id,
			contactId: contactId,
			name: billingInformation.name,
			billingAddress: {
				type: billingInformation.billingAddress.type,
				city: billingInformation.billingAddress.city,
				country: billingInformation.billingAddress.country,
				state: billingInformation.billingAddress.state,
				street: billingInformation.billingAddress.street,
				zipCode: billingInformation.billingAddress.zipCode,
				name: billingInformation.billingAddress.name ?? "",
			},
			shippingAddress: {
				type: billingInformation.shippingAddress.type,
				city: billingInformation.shippingAddress.city,
				country: billingInformation.shippingAddress.country,
				state: billingInformation.shippingAddress.state,
				street: billingInformation.shippingAddress.street,
				zipCode: billingInformation.shippingAddress.zipCode,
				name: billingInformation.shippingAddress.name ?? "",
			},
			bankAccount: {
				bankName: billingInformation.bankAccount.bankName,
				bic: billingInformation.bankAccount.bic,
				clientReference: billingInformation.bankAccount.clientReference,
				clientReferenceDate: billingInformation.bankAccount.clientReferenceDate,
				iban: billingInformation.bankAccount.iban,
			},
		};

		return updateBillingInformation({ variables: variables });
	};

	return [doWork];
};

export const useUpdatePaymentInformation = () => {
	const updatePaymentInformation = useMutationWithPromise(UPDATE_PAYMENT_INFORMATION);

	const doWork = async (contactId: string, paymentInformation: PaymentInformation) => {
		updatePaymentInformation({
			id: paymentInformation.id,
			contactId: contactId,
			taxNumber: paymentInformation.taxNumber,
			paymentDeadlineInDays: paymentInformation.paymentDeadlineInDays,
			routeId: paymentInformation.routeId,
			vatId: paymentInformation.vatId,
		});
	};

	return [doWork];
};

export const useUpdatePhoneInformation = () => {
	const updatePhoneInformation = useMutationWithPromise(UPDATE_PHONE_INFORMATION);

	const doWork = async (contactId: string, phoneInformation: PhoneInformation) => {
		updatePhoneInformation({
			id: phoneInformation.id,
			contactId: contactId,
			number: phoneInformation.number,
			phoneType: phoneInformation.phoneType,
		});
	};

	return [doWork];
};

export const useUpdateContractedInformation = () => {
	const updateContractedInformation = useMutationWithPromise(UPDATE_CONTRACTED_INFORMATION);

	const doWork = async (contactId: string, contractedInformation: ContractedInformation) => {
		updateContractedInformation({
			contactInformationId: contractedInformation.id,
			contactId: contactId,
			contractedId: contractedInformation.contractedId,
			margin: contractedInformation.marge,
		});
	};

	return [doWork];
};

export const useUpdateSalaryInformation = () => {
	const updateSalaryInformation = useMutationWithPromise(UPDATE_SALARY_INFORMATION);

	const doWork = async (contactId: string, salaryInformation: SalaryInformation) => {
		updateSalaryInformation({
			id: salaryInformation.id,
			contactId: contactId,
			pricePerHour: salaryInformation.pricePerHour,
			startDate: salaryInformation.startDate,
		});
	};

	return [doWork];
};

export const useUpdateWorkingConditionInformation = () => {
	const updateWorkingConditionInformation = useMutationWithPromise(
		UPDATE_WORKING_CONDITION_INFORMATION,
	);

	const doWork = async (
		contactId: string,
		workingConditionInformation: WorkingConditionInformation,
	) => {
		updateWorkingConditionInformation({
			id: workingConditionInformation.id,
			contactId: contactId,
			hoursPerFriday: workingConditionInformation.hoursPerFriday,
			hoursPerMonday: workingConditionInformation.hoursPerMonday,
			hoursPerSaturday: workingConditionInformation.hoursPerSaturday,
			hoursPerSunday: workingConditionInformation.hoursPerSunday,
			hoursPerThursday: workingConditionInformation.hoursPerThursday,
			hoursPerTuesday: workingConditionInformation.hoursPerTuesday,
			hoursPerWednesday: workingConditionInformation.hoursPerWednesday,
			hoursPerWeek: workingConditionInformation.hoursPerWeek,
			isFlexible: workingConditionInformation.isFlexible,
			startDate: workingConditionInformation.startDate,
		});
	};

	return [doWork];
};

export const useUpdateContactEmail = () => {
	const updateContactEmail = useMutationWithPromise(UPDATE_CONTACT_EMAIL);

	const doWork = async (contactId: string, email: EmailInformation) => {
		updateContactEmail({
			contactId: contactId,
			emailId: email.id,
			emailAddress: email.emailAddress,
			emailType: email.emailType,
		});
	};

	return [doWork];
};

export const useUpdateContactAddress = () => {
	const updateContactAddress = useMutationWithPromise(UPDATE_CONTACT_ADDRESS);

	const doWork = async (contactId: string, address: AddressInformation) => {
		updateContactAddress({
			contactId: contactId,
			addressId: address.id,
			type: address.type,
			name: address.name,
			city: address.city,
			country: address.country,
			state: address.state,
			street: address.street,
			zipCode: address.zipCode,
		});
	};

	return [doWork];
};

export const useDeleteContactEmail = () => {
	const deleteContactEmail = useMutationWithPromise(DELETE_ADDITIONAL_INFORMATION);

	const doWork = async (contactId: string, emailId: string) => {
		deleteContactEmail({
			contactId: contactId,
			informationId: emailId,
		});
	};

	return [doWork];
};

export const useDeleteContactAddress = () => {
	const deleteContactAddress = useMutationWithPromise(DELETE_ADDITIONAL_INFORMATION);

	const doWork = async (contactId: string, addressId: string) => {
		deleteContactAddress({
			contactId: contactId,
			informationId: addressId,
		});
	};

	return [doWork];
};

export const useDeleteContactBankAccount = () => {
	const deleteContactBankAccount = useMutationWithPromise(DELETE_ADDITIONAL_INFORMATION);

	const doWork = async (contactId: string, bankAccountId: string) => {
		deleteContactBankAccount({
			contactId: contactId,
			informationId: bankAccountId,
		});
	};

	return [doWork];
};

export const useDeleteContactPersonInformation = () => {
	const deleteContactPersonInformation = useMutationWithPromise(DELETE_ADDITIONAL_INFORMATION);

	const doWork = async (contactId: string, personInformationId: string) => {
		deleteContactPersonInformation({
			contactId: contactId,
			informationId: personInformationId,
		});
	};

	return [doWork];
};

export const useDeleteBillingInformation = () => {
	const deleteBillingInformation = useMutationWithPromise(DELETE_ADDITIONAL_INFORMATION);

	const doWork = async (contactId: string, billingInformationId: string) => {
		deleteBillingInformation({
			contactId: contactId,
			informationId: billingInformationId,
		});
	};

	return [doWork];
};

export const useDeletePaymentInformation = () => {
	const deletePaymentInformation = useMutationWithPromise(DELETE_ADDITIONAL_INFORMATION);

	const doWork = async (contactId: string, paymentInformationId: string) => {
		deletePaymentInformation({
			contactId: contactId,
			informationId: paymentInformationId,
		});
	};

	return [doWork];
};

export const useDeletePhoneInformation = () => {
	const deletePhoneInformation = useMutationWithPromise(DELETE_ADDITIONAL_INFORMATION);

	const doWork = async (contactId: string, phoneInformationId: string) => {
		deletePhoneInformation({
			contactId: contactId,
			informationId: phoneInformationId,
		});
	};

	return [doWork];
};

export const useDeleteSalaryInformation = () => {
	const deleteSalaryInformation = useMutationWithPromise(DELETE_ADDITIONAL_INFORMATION);

	const doWork = async (contactId: string, salaryInformationId: string) => {
		deleteSalaryInformation({
			contactId: contactId,
			informationId: salaryInformationId,
		});
	};

	return [doWork];
};

export const useDeleteWorkingConditionInformation = () => {
	const deleteWorkingConditionInformation = useMutationWithPromise(DELETE_ADDITIONAL_INFORMATION);

	const doWork = async (contactId: string, workingConditionInformationId: string) => {
		deleteWorkingConditionInformation({
			contactId: contactId,
			informationId: workingConditionInformationId,
		});
	};

	return [doWork];
};

export const useDeleteContractedInformation = () => {
	const deleteContractedInformation = useMutationWithPromise(DELETE_ADDITIONAL_INFORMATION);

	const doWork = async (contactId: string, contractedInformationId: string) => {
		deleteContractedInformation({
			contactId: contactId,
			informationId: contractedInformationId,
		});
	};

	return [doWork];
};

export const useDeleteInternalWorkingCondition = () => {
	const deleteInternalWorkingCondition = useMutationWithPromise(DELETE_ADDITIONAL_INFORMATION);

	const doWork = async (contactId: string, internalWorkingConditionId: string) => {
		deleteInternalWorkingCondition({
			contactId: contactId,
			informationId: internalWorkingConditionId,
		});
	};

	return [doWork];
};

export const useDeleteWebsiteInformation = () => {
	const deleteWebsiteInformation = useMutationWithPromise(DELETE_ADDITIONAL_INFORMATION);

	const doWork = async (contactId: string, websiteInformationId: string) => {
		deleteWebsiteInformation({
			contactId: contactId,
			informationId: websiteInformationId,
		});
	};

	return [doWork];
};

export const useDeletePaymentConditionInformation = () => {
	const deletePaymentConditionInformation = useMutationWithPromise(DELETE_ADDITIONAL_INFORMATION);

	const doWork = async (contactId: string, paymentConditionInformationId: string) => {
		deletePaymentConditionInformation({
			contactId: contactId,
			informationId: paymentConditionInformationId,
		});
	};

	return [doWork];
};

export const useDeleteIsContactPersonOfInformation = () => {
	const deleteIsContactPersonOfInformation = useMutationWithPromise(DELETE_ADDITIONAL_INFORMATION);

	const doWork = async (contactId: string, isContactPersonOfInformationId: string) => {
		deleteIsContactPersonOfInformation({
			contactId: contactId,
			informationId: isContactPersonOfInformationId,
		});
	};

	return [doWork];
};

export const useDeleteDateInformation = () => {
	const deleteDateInformation = useMutationWithPromise(DELETE_ADDITIONAL_INFORMATION);

	const doWork = async (contactId: string, dateInformationId: string) => {
		deleteDateInformation({
			contactId: contactId,
			informationId: dateInformationId,
		});
	};

	return [doWork];
};

export const useCreateWebsiteInformation = () => {
	const createWebsiteInformation = useMutationWithPromise(CREATE_WEBSITE_INFORMATION);

	const doWork = async (contactId: string, websiteInformation: WebsiteInformation) => {
		createWebsiteInformation({
			contactId: contactId,
			url: websiteInformation.url,
		});
	};

	return [doWork];
};

export const useUpdateWebsiteInformation = () => {
	const updateWebsiteInformation = useMutationWithPromise(UPDATE_WEBSITE_INFORMATION);

	const doWork = async (contactId: string, websiteInformation: WebsiteInformation) => {
		updateWebsiteInformation({
			id: websiteInformation.id,
			contactId: contactId,
			url: websiteInformation.url,
		});
	};

	return [doWork];
};

export const useCreatePaymentConditionInformation = () => {
	const createPaymentConditionInformation = useMutationWithPromise(
		CREATE_PAYMENT_CONDITION_INFORMATION,
	);

	const doWork = async (
		contactId: string,
		paymentConditionInformation: PaymentConditionInformation,
	) => {
		if (
			!paymentConditionInformation.discountProfileId?.id ||
			!paymentConditionInformation.selectedEntryId
		) {
			return;
		}
		createPaymentConditionInformation({
			contactId: contactId,
			discountProfileId: paymentConditionInformation.discountProfileId,
			selectedEntryId: paymentConditionInformation.selectedEntryId,
			doesNotParticipateInVoucherProcess:
				paymentConditionInformation.doesNotParticipateInVoucherProcess ?? false,
		});
	};

	return [doWork];
};

export const useUpdatePaymentConditionInformation = () => {
	const updatePaymentConditionInformation = useMutationWithPromise(
		UPDATE_PAYMENT_CONDITION_INFORMATION,
	);

	const doWork = async (
		contactId: string,
		paymentConditionInformation: PaymentConditionInformation,
	) => {
		if (
			!paymentConditionInformation.discountProfileId?.id ||
			!paymentConditionInformation.selectedEntryId
		) {
			return;
		}
		updatePaymentConditionInformation({
			id: paymentConditionInformation.id,
			contactId: contactId,
			discountProfileId: paymentConditionInformation.discountProfileId,
			selectedEntryId: paymentConditionInformation.selectedEntryId,
			doesNotParticipateInVoucherProcess:
				paymentConditionInformation.doesNotParticipateInVoucherProcess ?? false,
		});
	};

	return [doWork];
};

// export const useCreateIsContactPersonOfInformation = () => {
// 	const createIsContactPersonOfInformation = useMutationWithPromise(
// 		CREATE_IS_CONTACT_PERSON_OF_INFORMATION,
// 	);

// 	const doWork = async (
// 		contactId: string,
// 		isContactPersonOfInformation: IsContactPersonOfInformation,
// 	) => {
// 		createIsContactPersonOfInformation({
// 			contactId: contactId,
// 			contactPersonId: isContactPersonOfInformation.contactPersonId,
// 		});
// 	};

// 	return [doWork];
// };

// export const useUpdateIsContactPersonOfInformation = () => {
// 	const updateIsContactPersonOfInformation = useMutationWithPromise(
// 		UPDATE_IS_CONTACT_PERSON_OF_INFORMATION,
// 	);

// 	const doWork = async (
// 		contactId: string,
// 		isContactPersonOfInformation: IsContactPersonOfInformation,
// 	) => {
// 		updateIsContactPersonOfInformation({
// 			id: isContactPersonOfInformation.id,
// 			contactId: contactId,
// 			contactPersonId: isContactPersonOfInformation.contactPersonId,
// 		});
// 	};

// 	return [doWork];
// };

export const useCreateDateInformation = () => {
	const createDateInformation = useMutationWithPromise(CREATE_DATE_INFORMATION);

	const doWork = async (contactId: string, dateInformation: DateInformation) => {
		createDateInformation({
			contactId: contactId,
			date: dateInformation.date,
			dateType: dateInformation.personDateType,
		});
	};

	return [doWork];
};

export const useUpdateDateInformation = () => {
	const updateDateInformation = useMutationWithPromise(UPDATE_DATE_INFORMATION);
	const doWork = async (contactId: string, dateInformation: DateInformation) => {
		updateDateInformation({
			id: dateInformation.id,
			contactId: contactId,
			date: dateInformation.date,
			dateType: dateInformation.personDateType,
		});
	};

	return [doWork];
};

export const useCreateDepartmentInformation = () => {
	const createDepartmentInformation = useMutationWithPromise(CREATE_DEPARTMENT_INFORMATION);

	const doWork = async (contactId: string, departmentInformation: DepartmentInformation) => {
		createDepartmentInformation({
			contactId: contactId,
			departmentName: departmentInformation.departmentName,
		});
	};

	return [doWork];
};

export const useUpdateDepartmentInformation = () => {
	const updateDepartmentInformation = useMutationWithPromise(UPDATE_DEPARTMENT_INFORMATION);

	const doWork = async (contactId: string, departmentInformation: DepartmentInformation) => {
		updateDepartmentInformation({
			id: departmentInformation.id,
			contactId: contactId,
			departmentName: departmentInformation.departmentName,
		});
	};

	return [doWork];
};

export const useDeleteDepartmentInformation = () => {
	const deleteDepartmentInformation = useMutationWithPromise(DELETE_ADDITIONAL_INFORMATION);

	const doWork = async (contactId: string, DepartmentInformationId: string) => {
		deleteDepartmentInformation({
			contactId: contactId,
			informationId: DepartmentInformationId,
		});
	};

	return [doWork];
};

interface ContactAction<T> {
	create: (contactId: string, data: T) => Promise<void>;
	update?: (contactId: string, data: T) => Promise<void>;
	delete?: (contactId: string, id: string) => Promise<void>;
}

export const useCreateCompanyContact = () => {
	const createContact = useMutationWithPromise(CREATE_COMPANY_CONTACT);
	const [addContactEmails] = useCreateContactEmails();
	const [addContactAddresses] = useCreateContactAddresses();
	const [addContactPersonInformation] = useCreateContactPersonInformation();
	const [addBillingInformation] = useCreateBillingInformation();
	const [addPaymentInformation] = useCreatePaymentInformation();
	const [addPhoneInformation] = useCreatePhoneInformation();
	const [addSalaryInformation] = useCreateSalaryInformation();
	const [addWorkingConditionInformation] = useCreateWorkingConditionInformation();
	const [addContractedInformation] = useCreateContractedInformation();
	const [addInternalWorkingCondition] = useCreateEmployeeWorkingConditionInformation();
	const [addPaymentConditionInformation] = useCreatePaymentConditionInformation();
	// const [addIsContactPersonOfInformation] =
	// 	useCreateIsContactPersonOfInformation();
	const [addDepartmentInformation] = useCreateDepartmentInformation();
	const [addDateInformation] = useCreateDateInformation();
	const [addWebsiteInformation] = useCreateWebsiteInformation();

	const actions = {
		EmailInformation: {
			create: addContactEmails,
		} as ContactAction<EmailInformation>,
		AddressInformation: {
			create: addContactAddresses,
		} as ContactAction<AddressInformation>,
		BillingInformation: {
			create: addBillingInformation,
		} as ContactAction<BillingInformation>,
		ContactPersonInformation: {
			create: addContactPersonInformation,
		} as ContactAction<ContactPersonInformation>,
		PaymentInformation: {
			create: addPaymentInformation,
		} as ContactAction<PaymentInformation>,
		PhoneInformation: {
			create: addPhoneInformation,
		} as ContactAction<PhoneInformation>,
		SalaryInformation: {
			create: addSalaryInformation,
		} as ContactAction<SalaryInformation>,
		WorkingConditionInformation: {
			create: addWorkingConditionInformation,
		} as ContactAction<WorkingConditionInformation>,
		ContractedInformation: {
			create: addContractedInformation,
		} as ContactAction<ContractedInformation>,
		EmployeeWorkingConditionInformation: {
			create: addInternalWorkingCondition,
		} as ContactAction<EmployeeWorkingConditionInformation>,
		DepartmentInformation: {
			create: addDepartmentInformation,
		} as ContactAction<DepartmentInformation>,
		WebsiteInformation: {
			create: addWebsiteInformation,
		} as ContactAction<WebsiteInformation>,
		PaymentConditionInformation: {
			create: addPaymentConditionInformation,
		} as ContactAction<PaymentConditionInformation>,
		// IsContactPersonOfInformation: {
		// 	create: addIsContactPersonOfInformation,
		// } as ContactAction<IsContactPersonOfInformation>,
		DateInformation: {
			create: addDateInformation,
		} as ContactAction<DateInformation>,
		null: null,
	};

	async function processContactAction<T>(contactId: string, action: ContactAction<T>, data: T) {
		await action.create(contactId, data);
	}

	async function handleContactAction(
		contact: Contact,
		a:
			| EmailInformation
			| AddressInformation
			| BillingInformation
			| ContactPersonInformation
			| PaymentInformation
			| PhoneInformation
			| SalaryInformation
			| WorkingConditionInformation
			| ContractedInformation
			| EmployeeWorkingConditionInformation
			| DepartmentInformation
			| WebsiteInformation
			| PaymentConditionInformation
			| IsContactPersonOfInformation
			| DepartmentInformation
			| DateInformation,
	) {
		const action = actions[a.__typename ?? "null"];

		if (action) {
			await processContactAction(contact.id, action, a);
		}
	}

	const doWork = async (contact: Contact | null) => {
		if (!contact) return null;
		const data = (await createContact({
			companyName: contact.companyName,
			contactType: findExactSingleContactType(contact),
		})) as { createCompanyContact: { contact: { id: string } } };
		contact.id = data.createCompanyContact.contact.id;

		if (contact.additionalInformation.length === 0) {
			return data.createCompanyContact.contact.id;
		}

		return await Promise.all(
			contact.additionalInformation.map(async (a) => {
				await handleContactAction(
					contact,
					a as
						| EmailInformation
						| AddressInformation
						| BillingInformation
						| ContactPersonInformation
						| PaymentInformation
						| PhoneInformation
						| SalaryInformation
						| WorkingConditionInformation
						| ContractedInformation
						| EmployeeWorkingConditionInformation
						| DepartmentInformation
						| WebsiteInformation
						| PaymentConditionInformation
						| IsContactPersonOfInformation
						| DepartmentInformation
						| DateInformation,
				);
			}),
		)
			.then(() => {
				return data.createCompanyContact.contact.id;
			})
			.catch((err) => {
				return err;
			});
	};

	return [doWork];
};

export const useCreatePersonContact = () => {
	const createContact = useMutationWithPromise(CREATE_PERSON_CONTACT);
	const [addContactEmails] = useCreateContactEmails();
	const [addContactAddresses] = useCreateContactAddresses();
	const [addContactPersonInformation] = useCreateContactPersonInformation();
	const [addBillingInformation] = useCreateBillingInformation();
	const [addPaymentInformation] = useCreatePaymentInformation();
	const [addPhoneInformation] = useCreatePhoneInformation();
	const [addSalaryInformation] = useCreateSalaryInformation();
	const [addWorkingConditionInformation] = useCreateWorkingConditionInformation();
	const [addInternalWorkingCondition] = useCreateEmployeeWorkingConditionInformation();
	const [addContractedInformation] = useCreateContractedInformation();
	const [addDepartmentInformation] = useCreateDepartmentInformation();
	const [addPaymentConditionInformation] = useCreatePaymentConditionInformation();
	// const [addIsContactPersonOfInformation] =
	// 	useCreateIsContactPersonOfInformation();
	const [addDateInformation] = useCreateDateInformation();
	const [addWebsiteInformation] = useCreateWebsiteInformation();

	const actions: {
		EmailInformation: ContactAction<EmailInformation>;
		AddressInformation: ContactAction<AddressInformation>;
		ContactPersonInformation: ContactAction<ContactPersonInformation>;
		BillingInformation: ContactAction<BillingInformation>;
		PaymentInformation: ContactAction<PaymentInformation>;
		PhoneInformation: ContactAction<PhoneInformation>;
		SalaryInformation: ContactAction<SalaryInformation>;
		WorkingConditionInformation: ContactAction<WorkingConditionInformation>;
		EmployeeWorkingConditionInformation: ContactAction<EmployeeWorkingConditionInformation>;
		ContractedInformation: ContactAction<ContractedInformation>;
		DepartmentInformation: ContactAction<DepartmentInformation>;
		WebsiteInformation: ContactAction<WebsiteInformation>;
		PaymentConditionInformation: ContactAction<PaymentConditionInformation>;
		// IsContactPersonOfInformation: ContactAction<IsContactPersonOfInformation>;
		DateInformation: ContactAction<DateInformation>;
		null: null;
	} = {
		EmailInformation: {
			create: addContactEmails,
		} as ContactAction<EmailInformation>,
		AddressInformation: {
			create: addContactAddresses,
		} as ContactAction<AddressInformation>,
		BillingInformation: {
			create: addBillingInformation,
		} as ContactAction<BillingInformation>,
		ContactPersonInformation: {
			create: addContactPersonInformation,
		} as ContactAction<ContactPersonInformation>,
		PaymentInformation: {
			create: addPaymentInformation,
		} as ContactAction<PaymentInformation>,
		PhoneInformation: {
			create: addPhoneInformation,
		} as ContactAction<PhoneInformation>,
		SalaryInformation: {
			create: addSalaryInformation,
		} as ContactAction<SalaryInformation>,
		WorkingConditionInformation: {
			create: addWorkingConditionInformation,
		} as ContactAction<WorkingConditionInformation>,
		ContractedInformation: {
			create: addContractedInformation,
		} as ContactAction<ContractedInformation>,
		EmployeeWorkingConditionInformation: {
			create: addInternalWorkingCondition,
		} as ContactAction<EmployeeWorkingConditionInformation>,
		DepartmentInformation: {
			create: addDepartmentInformation,
		} as ContactAction<DepartmentInformation>,
		WebsiteInformation: {
			create: addWebsiteInformation,
		} as ContactAction<WebsiteInformation>,
		PaymentConditionInformation: {
			create: addPaymentConditionInformation,
		} as ContactAction<PaymentConditionInformation>,
		// IsContactPersonOfInformation: {
		// 	create: addIsContactPersonOfInformation,
		// } as ContactAction<IsContactPersonOfInformation>,
		DateInformation: {
			create: addDateInformation,
		} as ContactAction<DateInformation>,
		null: null,
	};

	async function processContactAction<T>(contactId: string, action: ContactAction<T>, data: T) {
		await action.create(contactId, data);
	}

	async function handleContactAction<T>(
		contact: Contact,
		a: T & { id: string; __typename?: string },
	) {
		const action = a.__typename ? actions[a.__typename] : null;

		if (action) {
			await processContactAction(contact.id, action, a);
		}
	}

	const doWork = async (contact: Contact | null, memberOfCompanyId: string | null) => {
		if (!contact) return null;
		const data = (await createContact({
			firstName: contact.firstName,
			lastName: contact.lastName,
			contactType: findExactSingleContactType(contact),
			salutation: contact.salutation,
			title: contact.title,
			memberOfCompanyId: memberOfCompanyId,
			shortCode: contact.shortCode,
		})) as { createPersonContact: { contact: { id: string } } };
		contact.id = data.createPersonContact.contact.id;

		if (contact.additionalInformation.length === 0) {
			return data.createPersonContact.contact;
		}

		return await Promise.all(
			contact.additionalInformation.map(async (a) => {
				await handleContactAction(
					contact,
					a as
						| EmailInformation
						| AddressInformation
						| BillingInformation
						| ContactPersonInformation
						| PaymentInformation
						| PhoneInformation
						| SalaryInformation
						| WorkingConditionInformation
						| ContractedInformation
						| EmployeeWorkingConditionInformation
						| DepartmentInformation
						| WebsiteInformation
						| PaymentConditionInformation
						| IsContactPersonOfInformation
						| DepartmentInformation
						| DateInformation,
				);
			}),
		)
			.then(() => {
				return data.createPersonContact.contact;
			})
			.catch((err) => {
				return err;
			});
	};

	return [doWork];
};

// Updates a Company Contact
export const useUpdateCompanyContact = () => {
	const updateContact = useMutationWithPromise(UPDATE_COMPANY_CONTACT);
	const [addContactEmails] = useCreateContactEmails();
	const [updateContactEmail] = useUpdateContactEmail();
	const [deleteContactEmail] = useDeleteContactEmail();
	const [addContactAddresses] = useCreateContactAddresses();
	const [updateContactAddress] = useUpdateContactAddress();
	const [deleteContactAddress] = useDeleteContactAddress();
	const [addContactPersonInformation] = useCreateContactPersonInformation();
	const [updateContactPersonInformation] = useUpdateContactPersonInformation();
	const [deleteContactPersonInformation] = useDeleteContactPersonInformation();
	const [addBillingInformation] = useCreateBillingInformation();
	const [updateBillingInformation] = useUpdateBillingInformation();
	const [deleteBillingInformation] = useDeleteBillingInformation();
	const [addPaymentInformation] = useCreatePaymentInformation();
	const [updatePaymentInformation] = useUpdatePaymentInformation();
	const [deletePaymentInformation] = useDeletePaymentInformation();
	const [addPhoneInformation] = useCreatePhoneInformation();
	const [updatePhoneInformation] = useUpdatePhoneInformation();
	const [deletePhoneInformation] = useDeletePhoneInformation();
	const [addSalaryInformation] = useCreateSalaryInformation();
	const [updateSalaryInformation] = useUpdateSalaryInformation();
	const [deleteSalaryInformation] = useDeleteSalaryInformation();
	const [addWorkingConditionInformation] = useCreateWorkingConditionInformation();
	const [updateWorkingConditionInformation] = useUpdateWorkingConditionInformation();
	const [deleteWorkingConditionInformation] = useDeleteWorkingConditionInformation();
	const [addContractedInformation] = useCreateContractedInformation();
	const [updateContractedInformation] = useUpdateContractedInformation();
	const [deleteContractedInformation] = useDeleteContractedInformation();
	const [addInternalWorkingCondition] = useCreateEmployeeWorkingConditionInformation();
	const [updateInternalWorkingCondition] = useUpdateEmployeeWorkingConditionInformation();
	const [deleteInternalWorkingCondition] = useDeleteInternalWorkingCondition();
	const [addWebsiteInformation] = useCreateWebsiteInformation();
	const [updateWebsiteInformation] = useUpdateWebsiteInformation();
	const [deleteWebsiteInformation] = useDeleteWebsiteInformation();
	const [addPaymentConditionInformation] = useCreatePaymentConditionInformation();
	const [updatePaymentConditionInformation] = useUpdatePaymentConditionInformation();
	const [deletePaymentConditionInformation] = useDeletePaymentConditionInformation();
	// const [addIsContactPersonOfInformation] =
	// 	useCreateIsContactPersonOfInformation();
	// const [updateIsContactPersonOfInformation] =
	// 	useUpdateIsContactPersonOfInformation();
	// const [deleteIsContactPersonOfInformation] =
	useDeleteIsContactPersonOfInformation();
	const [addDepartmentInformation] = useCreateDepartmentInformation();
	const [updateDepartmentInformation] = useUpdateDepartmentInformation();
	const [deleteDepartmentInformation] = useDeleteDepartmentInformation();
	const [addDateInformation] = useCreateDateInformation();
	const [updateDateInformation] = useUpdateDateInformation();
	const [deleteDateInformation] = useDeleteDateInformation();

	const doWork = async (contact: Contact | null) => {
		if (!contact) return null;
		const data = (await updateContact({
			id: contact.id,
			companyName: contact.companyName,
			contactNumber: contact.contactNumber,
			contactType: findExactSingleContactType(contact),
		})) as { updateCompanyContact: { contact: Contact } };

		const actions = {
			EmailInformation: {
				create: addContactEmails,
				update: updateContactEmail,
				delete: deleteContactEmail,
			} as ContactAction<EmailInformation>,
			AddressInformation: {
				create: addContactAddresses,
				update: updateContactAddress,
				delete: deleteContactAddress,
			} as ContactAction<AddressInformation>,
			BillingInformation: {
				create: addBillingInformation,
				update: updateBillingInformation,
				delete: deleteBillingInformation,
			} as ContactAction<BillingInformation>,
			ContactPersonInformation: {
				create: addContactPersonInformation,
				update: updateContactPersonInformation,
				delete: deleteContactPersonInformation,
			} as ContactAction<ContactPersonInformation>,
			PaymentInformation: {
				create: addPaymentInformation,
				update: updatePaymentInformation,
				delete: deletePaymentInformation,
			} as ContactAction<PaymentInformation>,
			PhoneInformation: {
				create: addPhoneInformation,
				update: updatePhoneInformation,
				delete: deletePhoneInformation,
			} as ContactAction<PhoneInformation>,
			SalaryInformation: {
				create: addSalaryInformation,
				update: updateSalaryInformation,
				delete: deleteSalaryInformation,
			} as ContactAction<SalaryInformation>,
			WorkingConditionInformation: {
				create: addWorkingConditionInformation,
				update: updateWorkingConditionInformation,
				delete: deleteWorkingConditionInformation,
			} as ContactAction<WorkingConditionInformation>,
			ContractedInformation: {
				create: addContractedInformation,
				update: updateContractedInformation,
				delete: deleteContractedInformation,
			} as ContactAction<ContractedInformation>,
			EmployeeWorkingConditionInformation: {
				create: addInternalWorkingCondition,
				update: updateInternalWorkingCondition,
				delete: deleteInternalWorkingCondition,
			} as ContactAction<EmployeeWorkingConditionInformation>,
			DepartmentInformation: {
				create: addDepartmentInformation,
				update: updateDepartmentInformation,
				delete: deleteDepartmentInformation,
			} as ContactAction<DepartmentInformation>,
			WebsiteInformation: {
				create: addWebsiteInformation,
				update: updateWebsiteInformation,
				delete: deleteWebsiteInformation,
			} as ContactAction<WebsiteInformation>,
			PaymentConditionInformation: {
				create: addPaymentConditionInformation,
				update: updatePaymentConditionInformation,
				delete: deletePaymentConditionInformation,
			} as ContactAction<PaymentConditionInformation>,
			// IsContactPersonOfInformation: {
			// 	create: addIsContactPersonOfInformation,
			// 	update: updateIsContactPersonOfInformation,
			// 	delete: deleteIsContactPersonOfInformation,
			// } as ContactAction<IsContactPersonOfInformation>,
			DateInformation: {
				create: addDateInformation,
				update: updateDateInformation,
				delete: deleteDateInformation,
			} as ContactAction<DateInformation>,
			null: null,
		};

		async function processContactAction<T>(
			contactId: string,
			action: ContactAction<T>,
			data: T & { id: string },
			existing: string[],
			deleted: string[],
		) {
			if (!existing.includes(data.id)) {
				await action.create(contactId, data);
			} else if (deleted.includes(data.id) && action.delete) {
				await action.delete(contactId, data.id);
			} else if (action.update) {
				await action.update(contactId, data);
			}
		}

		async function handleContactAction<T>(
			contact: Contact,
			a: T & { id: string; __typename?: string },
			existing: string[],
			deleted: string[],
		) {
			const action = a.__typename ? actions[a.__typename] : null;

			if (action) {
				await processContactAction(contact.id, action, a, existing, deleted);
			}
		}

		const existing = data.updateCompanyContact.contact.additionalInformation
			.map(({ id }) => id)
			.filter((id): id is string => !!id);
		const deleted = existing.filter(
			(id) => !contact.additionalInformation.find((a) => a.id === id),
		);

		const response = await Promise.all(
			contact.additionalInformation.map(async (a) => {
				return await handleContactAction(contact, a, existing, deleted);
			}),
		)
			.then(() => {
				return data.updateCompanyContact.contact.id;
			})
			.catch((err) => {
				return err;
			});

		return response;
	};

	return [doWork];
};

// Updates a Person Contact
export const useUpdatePersonContact = () => {
	const updateContact = useMutationWithPromise(UPDATE_PERSON_CONTACT);
	const [addContactEmails] = useCreateContactEmails();
	const [updateContactEmail] = useUpdateContactEmail();
	const [deleteContactEmail] = useDeleteContactEmail();
	const [addContactAddresses] = useCreateContactAddresses();
	const [updateContactAddress] = useUpdateContactAddress();
	const [deleteContactAddress] = useDeleteContactAddress();
	const [addContactPersonInformation] = useCreateContactPersonInformation();
	const [updateContactPersonInformation] = useUpdateContactPersonInformation();
	const [deleteContactPersonInformation] = useDeleteContactPersonInformation();
	const [addBillingInformation] = useCreateBillingInformation();
	const [updateBillingInformation] = useUpdateBillingInformation();
	const [deleteBillingInformation] = useDeleteBillingInformation();
	const [addPaymentInformation] = useCreatePaymentInformation();
	const [updatePaymentInformation] = useUpdatePaymentInformation();
	const [deletePaymentInformation] = useDeletePaymentInformation();
	const [addPhoneInformation] = useCreatePhoneInformation();
	const [updatePhoneInformation] = useUpdatePhoneInformation();
	const [deletePhoneInformation] = useDeletePhoneInformation();
	const [addSalaryInformation] = useCreateSalaryInformation();
	const [updateSalaryInformation] = useUpdateSalaryInformation();
	const [deleteSalaryInformation] = useDeleteSalaryInformation();
	const [addWorkingConditionInformation] = useCreateWorkingConditionInformation();
	const [updateWorkingConditionInformation] = useUpdateWorkingConditionInformation();
	const [deleteWorkingConditionInformation] = useDeleteWorkingConditionInformation();
	const [addContractedInformation] = useCreateContractedInformation();
	const [updateContractedInformation] = useUpdateContractedInformation();
	const [deleteContractedInformation] = useDeleteContractedInformation();
	const [addInternalWorkingCondition] = useCreateEmployeeWorkingConditionInformation();
	const [updateInternalWorkingCondition] = useUpdateEmployeeWorkingConditionInformation();
	const [deleteInternalWorkingCondition] = useDeleteInternalWorkingCondition();
	const [addWebsiteInformation] = useCreateWebsiteInformation();
	const [updateWebsiteInformation] = useUpdateWebsiteInformation();
	const [deleteWebsiteInformation] = useDeleteWebsiteInformation();
	const [addPaymentConditionInformation] = useCreatePaymentConditionInformation();
	const [updatePaymentConditionInformation] = useUpdatePaymentConditionInformation();
	const [deletePaymentConditionInformation] = useDeletePaymentConditionInformation();
	// const [addIsContactPersonOfInformation] =
	// 	useCreateIsContactPersonOfInformation();
	// const [updateIsContactPersonOfInformation] =
	// 	useUpdateIsContactPersonOfInformation();
	// const [deleteIsContactPersonOfInformation] =
	// 	useDeleteIsContactPersonOfInformation();
	const [addDepartmentInformation] = useCreateDepartmentInformation();
	const [updateDepartmentInformation] = useUpdateDepartmentInformation();
	const [deleteDepartmentInformation] = useDeleteDepartmentInformation();
	const [addDateInformation] = useCreateDateInformation();
	const [updateDateInformation] = useUpdateDateInformation();
	const [deleteDateInformation] = useDeleteDateInformation();

	const actions = {
		EmailInformation: {
			create: addContactEmails,
			update: updateContactEmail,
			delete: deleteContactEmail,
		} as ContactAction<EmailInformation>,
		AddressInformation: {
			create: addContactAddresses,
			update: updateContactAddress,
			delete: deleteContactAddress,
		} as ContactAction<AddressInformation>,
		BillingInformation: {
			create: addBillingInformation,
			update: updateBillingInformation,
			delete: deleteBillingInformation,
		} as ContactAction<BillingInformation>,
		ContactPersonInformation: {
			create: addContactPersonInformation,
			update: updateContactPersonInformation,
			delete: deleteContactPersonInformation,
		} as ContactAction<ContactPersonInformation>,
		PaymentInformation: {
			create: addPaymentInformation,
			update: updatePaymentInformation,
			delete: deletePaymentInformation,
		} as ContactAction<PaymentInformation>,
		PhoneInformation: {
			create: addPhoneInformation,
			update: updatePhoneInformation,
			delete: deletePhoneInformation,
		} as ContactAction<PhoneInformation>,
		SalaryInformation: {
			create: addSalaryInformation,
			update: updateSalaryInformation,
			delete: deleteSalaryInformation,
		} as ContactAction<SalaryInformation>,
		WorkingConditionInformation: {
			create: addWorkingConditionInformation,
			update: updateWorkingConditionInformation,
			delete: deleteWorkingConditionInformation,
		} as ContactAction<WorkingConditionInformation>,
		ContractedInformation: {
			create: addContractedInformation,
			update: updateContractedInformation,
			delete: deleteContractedInformation,
		} as ContactAction<ContractedInformation>,
		EmployeeWorkingConditionInformation: {
			create: addInternalWorkingCondition,
			update: updateInternalWorkingCondition,
			delete: deleteInternalWorkingCondition,
		} as ContactAction<EmployeeWorkingConditionInformation>,
		DepartmentInformation: {
			create: addDepartmentInformation,
			update: updateDepartmentInformation,
			delete: deleteDepartmentInformation,
		} as ContactAction<DepartmentInformation>,
		WebsiteInformation: {
			create: addWebsiteInformation,
			update: updateWebsiteInformation,
			delete: deleteWebsiteInformation,
		} as ContactAction<WebsiteInformation>,
		PaymentConditionInformation: {
			create: addPaymentConditionInformation,
			update: updatePaymentConditionInformation,
			delete: deletePaymentConditionInformation,
		} as ContactAction<PaymentConditionInformation>,
		// IsContactPersonOfInformation: {
		// 	create: addIsContactPersonOfInformation,
		// 	update: updateIsContactPersonOfInformation,
		// 	delete: deleteIsContactPersonOfInformation,
		// } as ContactAction<IsContactPersonOfInformation>,
		DateInformation: {
			create: addDateInformation,
			update: updateDateInformation,
			delete: deleteDateInformation,
		} as ContactAction<DateInformation>,
	};

	async function processContactAction<T>(
		contactId: string,
		action: ContactAction<T>,
		data: T & { id: string },
		existing: string[],
		deleted: string[],
	) {
		if (!existing.includes(data.id)) {
			await action.create(contactId, data);
		} else if (deleted.includes(data.id) && action.delete) {
			await action.delete(contactId, data.id);
		} else if (action.update) {
			await action.update(contactId, data);
		}
	}

	async function handleContactAction<T>(
		contact: Contact,
		a: T & { id: string; __typename?: string },
		existing: string[],
		deleted: string[],
	) {
		const action = a.__typename ? actions[a.__typename] : null;

		if (action) {
			await processContactAction(contact.id, action, a, existing, deleted);
		}
	}

	const doWork = async (contact: Contact | null) => {
		if (!contact) return null;
		const data = (await updateContact({
			id: contact.id,
			firstName: contact.firstName,
			lastName: contact.lastName,
			contactNumber: contact.contactNumber,
			contactType: findExactSingleContactType(contact),
			salutation: contact.salutation,
			title: contact.title,
			shortCode: contact.shortCode,
		} as unknown as Contact)) as { updatePersonContact: { contact: Contact } };
		const existing = data.updatePersonContact.contact.additionalInformation
			.map(({ id }) => id)
			.filter((id): id is string => !!id);
		const deleted = existing.filter(
			(id) => !contact.additionalInformation.find((a) => a.id === id),
		);

		const response = await Promise.all(
			contact.additionalInformation.map(async (a) => {
				return await handleContactAction(
					contact,
					a as
						| EmailInformation
						| AddressInformation
						| ContactPersonInformation
						| BillingInformation
						| PaymentInformation
						| PhoneInformation
						| SalaryInformation
						| WorkingConditionInformation
						| ContractedInformation
						| EmployeeWorkingConditionInformation
						| DepartmentInformation
						| WebsiteInformation
						| PaymentConditionInformation
						| IsContactPersonOfInformation
						| DepartmentInformation
						| DateInformation,
					existing,
					deleted,
				);
			}),
		)
			.then(async () => {
				return data.updatePersonContact.contact;
			})
			.catch((error) => {
				return error;
			});

		return response;
	};

	return [doWork];
};
